import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Tag, Loader2, AlertCircle, Clock, Twitter, Linkedin, Link as LinkIcon, CheckCircle2 } from 'lucide-react';
import CtaBanner from '../components/CtaBanner';
import NewsletterSignup from '../components/NewsletterSignup';
import Seo from '../components/Seo';
import { api, formatApiError } from '../lib/api';

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

function estimateReadingTime(html) {
  if (!html) return null;
  // Strip tags + entity references, then count whitespace-separated words.
  const text = String(html).replace(/<[^>]+>/g, ' ').replace(/&[a-z0-9#]+;/gi, ' ');
  const words = text.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 220)); // 220 wpm average
  return { minutes, words };
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setPost(null);
    setRelated([]);
    api.get(`/api/public/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch((err) => setError(err?.response?.status === 404 ? 'not_found' : formatApiError(err)))
      .finally(() => setLoading(false));
    // Pull a small list of other posts for the "related" block. We do a
    // lightweight client-side scoring against the loaded post's tags.
    api.get('/api/public/blog')
      .then((r) => setRelated(Array.isArray(r?.data?.items) ? r.data.items : []))
      .catch(() => setRelated([]));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const reading = useMemo(() => post && estimateReadingTime(post.content), [post]);

  const url = typeof window !== 'undefined' ? `${window.location.origin}/blog/${slug}` : `https://voipinger.com/blog/${slug}`;

  // Top 3 related posts: shared tag count first, then most recent.
  const relatedPicks = useMemo(() => {
    if (!post || !related.length) return [];
    const myTags = new Set((post.tags || []).map((t) => String(t).toLowerCase()));
    const ranked = related
      .filter((p) => p.slug && p.slug !== post.slug)
      .map((p) => {
        const overlap = (p.tags || []).reduce((n, t) => n + (myTags.has(String(t).toLowerCase()) ? 1 : 0), 0);
        return { p, overlap, ts: new Date(p.published_at || p.updated_at || 0).getTime() };
      })
      .sort((a, b) => b.overlap - a.overlap || b.ts - a.ts)
      .slice(0, 3)
      .map((x) => x.p);
    return ranked;
  }, [post, related]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500">
        <Loader2 size={18} className="animate-spin mr-2" /> Loading article…
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <AlertCircle size={40} className="text-violet-400 mb-3" />
        <h1 className="text-2xl font-extrabold mb-2">Article not found</h1>
        <p className="text-slate-500 mb-6">The post you are looking for may have been moved or unpublished.</p>
        <Link to="/blog" className="btn-primary">
          <ArrowLeft size={16} /> Back to blog
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <p className="text-rose-500">{error}</p>
      </div>
    );
  }

  // Schema.org Article payload — let the Seo helper emit JSON-LD so the
  // post gets eligible for Google rich results.
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.seo_title || post.title,
    description: post.seo_description || post.excerpt || '',
    image: post.cover_image || undefined,
    datePublished: post.published_at || undefined,
    dateModified: post.updated_at || post.published_at || undefined,
    author: { '@type': 'Organization', name: 'VoIPinger' },
    publisher: {
      '@type': 'Organization',
      name: 'VoIPinger',
      logo: { '@type': 'ImageObject', url: 'https://voipinger.com/assets/VoIPinger-Main-Logo-1.webp' },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    keywords: Array.isArray(post.tags) ? post.tags.join(', ') : undefined,
    inLanguage: 'en-GB',
  };

  return (
    <article className="bg-white">
      <Seo
        title={(post.seo_title || post.title) + ' — VoIPinger'}
        description={post.seo_description || post.excerpt || ''}
        path={`/blog/${slug}`}
        image={post.cover_image || undefined}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: post.title, path: `/blog/${slug}` },
        ]}
      />
      {/* Inject Article schema separately — the generic <Seo> covers
          Organization / FAQPage / Service, but Article needs the per-post
          metadata that only this component has. */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />

      {post.cover_image && (
        <div className="relative h-[45vh] min-h-[320px] overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 container-x pb-10">
            <Link
              to="/blog"
              data-testid="back-to-blog"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur border border-white/30 text-white text-sm font-semibold mb-4 transition-all"
            >
              <ArrowLeft size={14} /> Back to all articles
            </Link>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight max-w-4xl">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-5 mt-4 text-white/70 text-sm">
              <span className="flex items-center gap-2"><Calendar size={14} /> {formatDate(post.published_at)}</span>
              {reading && <span className="flex items-center gap-2"><Clock size={14} /> {reading.minutes} min read</span>}
              {(post.tags || []).length > 0 && (
                <span className="flex items-center gap-2"><Tag size={14} /> {post.tags.join(', ')}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {!post.cover_image && (
        <div className="container-x pt-32 pb-6">
          <Link
            to="/blog"
            data-testid="back-to-blog"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-violet-50 hover:bg-violet-100 border border-violet-200 text-violet-700 hover:text-violet-900 text-sm font-semibold mb-6 transition-all"
          >
            <ArrowLeft size={14} /> Back to all articles
          </Link>
          <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">{post.title}</h1>
          <div className="flex flex-wrap items-center gap-5 mt-4 text-slate-500 text-sm">
            <span className="flex items-center gap-2"><Calendar size={14} /> {formatDate(post.published_at)}</span>
            {reading && <span className="flex items-center gap-2"><Clock size={14} /> {reading.minutes} min read</span>}
          </div>
        </div>
      )}

      <div className="container-x py-12 md:py-16">
        {post.excerpt && (
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mb-8 border-l-4 border-violet-200 pl-5 italic">
            {post.excerpt}
          </p>
        )}

        <div
          className="blog-content max-w-3xl text-slate-700 text-[17px] leading-[1.8]"
          dangerouslySetInnerHTML={{ __html: post.content || '' }}
        />

        {/* Share rail */}
        <div className="max-w-3xl mt-12 pt-6 border-t border-slate-100 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Share</span>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(url)}`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 hover:bg-sky-50 border border-slate-200 text-slate-700 hover:text-sky-700 hover:border-sky-200 transition-colors text-xs font-bold"
            aria-label="Share on Twitter"
          >
            <Twitter size={12} /> Twitter
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 hover:bg-sky-50 border border-slate-200 text-slate-700 hover:text-[#0a66c2] hover:border-sky-200 transition-colors text-xs font-bold"
            aria-label="Share on LinkedIn"
          >
            <Linkedin size={12} /> LinkedIn
          </a>
          <button
            type="button"
            onClick={copyLink}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 hover:bg-violet-50 border border-slate-200 text-slate-700 hover:text-violet-700 hover:border-violet-200 transition-colors text-xs font-bold"
          >
            {copied ? <><CheckCircle2 size={12} className="text-emerald-600" /> Link copied</> : <><LinkIcon size={12} /> Copy link</>}
          </button>
        </div>

        {/* Newsletter card */}
        <div className="max-w-3xl mt-12">
          <NewsletterSignup variant="card" source={`blog:${slug}`} />
        </div>

        {/* Related posts */}
        {relatedPicks.length > 0 && (
          <div className="max-w-3xl mt-16 pt-10 border-t border-slate-100">
            <h2 className="text-xl font-extrabold text-slate-900 mb-5">Keep reading</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {relatedPicks.map((rp) => (
                <Link
                  key={rp.slug}
                  to={`/blog/${rp.slug}`}
                  className="group rounded-2xl border border-slate-200 overflow-hidden bg-white hover:border-violet-300 hover:shadow-md transition-all"
                >
                  <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                    {rp.cover_image ? (
                      <img src={rp.cover_image} alt={rp.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-violet-500 to-fuchsia-500" />
                    )}
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-slate-500 mb-1">{formatDate(rp.published_at)}</p>
                    <p className="font-bold text-slate-900 leading-tight line-clamp-2 group-hover:text-violet-700 transition-colors text-sm">{rp.title}</p>
                    <span className="text-violet-600 font-semibold text-xs flex items-center gap-1 mt-2">
                      Read article <ArrowRight size={12} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <CtaBanner
        title="Ready to upgrade your business communications?"
        subtitle="Start a 14-day free trial — no credit card, no long contracts."
        primary="Start free trial"
      />
    </article>
  );
}
